const enabled = value => value === true || value === 1 || value === '1';
const normalized = value => String(value || '').toLowerCase();

function cancellationRecord(row) {
  return ['cancelled', 'canceled'].includes(normalized(row.status)) || normalized(row.transaction_type) === 'cancellation';
}

function needsRenewalCheck(row) {
  return !!row && Number(row.pricing_model?.type) === 2 && !cancellationRecord(row) &&
    !['refund', 'chargeback'].includes(normalized(row.transaction_type)) && normalized(row.status) === 'captured';
}

// Shared by the name-cell indicators and the in-product legend.
const statusIndicators = {
  active: { icon: 'RepeatIcon', tone: 'active', label: 'Subscription active — this does not guarantee a scheduled or successful future payment' },
  cancelled: { icon: 'SlashIcon', tone: 'danger', label: 'Subscription cancelled — future renewals are cancelled' },
  inactive: { icon: 'MinusCircleIcon', tone: 'muted', label: 'Subscription inactive — this subscription is not currently active' },
  refunded: { icon: 'CornerUpLeftIcon', tone: 'refund', label: 'Payment refunded — this payment was fully refunded' },
  'refund-record': { icon: 'CornerUpLeftIcon', tone: 'refund', label: 'Refund record — this row records money returned to the customer' },
  'partial-refund': { icon: 'PieChartIcon', tone: 'warning', label: 'Partially refunded — only part of this payment was returned' },
  'refund-pending': { icon: 'RotateCcwIcon', tone: 'warning', label: 'Refund pending — the refund has not completed' },
  failed: { icon: 'XCircleIcon', tone: 'danger', label: 'Payment failed — this payment attempt was unsuccessful' },
  'cancelled-record': { icon: 'XIcon', tone: 'muted', label: 'Cancellation record — not a payment or refund' },
  prepared: { icon: 'ClockIcon', tone: 'muted', label: 'Payment not completed — a payment record exists, but payment has not been captured' },
  'payment-plan': { icon: 'CalendarIcon', tone: 'plan', label: 'Payment plan — current renewal status unavailable; open the transaction to review' },
  flagged: { icon: 'AlertTriangleIcon', tone: 'warning', label: 'Flagged for review — possible affiliate fraud, not a confirmed finding' },
  unavailable: { icon: 'InfoIcon', tone: 'unknown', label: 'Status unavailable — we could not verify the current subscription status; open the transaction to review' },
  checking: { icon: 'LoaderIcon', tone: 'muted', label: 'Checking subscription status…' },
};
const indicator = (name, key = name) => ({ ...statusIndicators[name], key });

function transactionBadges(row, renewal, loading = false) {
  if (!row) return [];
  const status = normalized(row.status);
  const badges = [];
  if (enabled(row.suspected_affiliate_fraud)) badges.push(indicator('flagged'));
  if (cancellationRecord(row)) return [...badges, indicator('cancelled-record')];
  if (status === 'refund_pending') {
    badges.push(indicator('refund-pending'));
  } else if (normalized(row.transaction_type) === 'refund' || status === 'refunded') {
    badges.push(indicator('refund-record'));
  } else if (enabled(row.is_partially_refunded)) {
    badges.push(indicator('partial-refund'));
  } else if (enabled(row.is_refunded)) {
    badges.push(indicator('refunded'));
  }
  if (status === 'failed') badges.push(indicator('failed'));
  if (status === 'prepared') badges.push(indicator('prepared'));
  if (needsRenewalCheck(row)) {
    const state = renewal || row.subscription_renewal;
    const renewalStatus = normalized(state?.status);
    const variant = ['active', 'cancelled', 'inactive'].includes(renewalStatus)
      ? renewalStatus : loading ? 'checking' : 'unavailable';
    badges.push(indicator(variant, 'subscription'));
  } else if (status === 'captured' && [3, 4].includes(Number(row.pricing_model?.type))) {
    // Historical installment counters cannot prove the current renewal state.
    badges.push(indicator('payment-plan'));
  }
  return badges;
}

function compactTransactionDate(row = {}) {
  return row.formatted_date || row.created_at_date || String(row.formatted_date_time || '').split(' - ')[0] || '—';
}

// Per-page cache, two concurrent GETs, no retries. Hidden/removed rows are skipped.
// Keep only renewal state, never copies of buyer or payment details.
function createRenewalReader(load) {
  let active = 0;
  let disposed = false;
  const pending = [];
  const cache = new Map();
  function pump() {
    while (active < 2 && pending.length) {
      const job = pending.shift();
      if (disposed || !job.isVisible()) { job.resolve(null); cache.delete(job.id); continue; }
      active += 1;
      Promise.resolve().then(() => load(job.id)).then(result => {
        job.resolve(result && String(result.id) === job.id ? result.subscription_renewal || null : null);
      }, () => job.resolve(null)).then(() => { active -= 1; pump(); });
    }
  }
  return {
    read(id, isVisible = () => true) {
      const key = String(id || '');
      if (disposed || !/^\d+$/.test(key)) return Promise.resolve(null);
      if (!cache.has(key)) {
        const promise = new Promise(resolve => pending.push({ id: key, resolve, isVisible }));
        cache.set(key, promise);
        pump();
      }
      return cache.get(key) || Promise.resolve(null);
    },
    dispose() { disposed = true; cache.clear(); while (pending.length) pending.shift().resolve(null); },
  };
}

module.exports = { statusIndicators, transactionBadges, compactTransactionDate, needsRenewalCheck, createRenewalReader };
